import { useT } from "../i18n/hooks.ts";
import { useDialogClose } from "./Dialog.tsx";

/**
 * Audit finding: dialog action rows were left-aligned with the primary
 * button on the LEFT (Guardar/Cancelar/…). Modern web+desktop convention
 * — the one Windows/macOS/GNOME/most web apps ship today — is primary on
 * the RIGHT, with the cancel/close (and optional secondary) sitting to
 * its LEFT. This wrapper is the single funnel every dialog goes through
 * (Simulación/Georref/Imagen fondo/Perfil and 14 others), so both the
 * JSX order below and the `.dialog-actions` CSS rule (which pins the
 * row to the right edge) apply uniformly — no dialog opts out. Cancel
 * comes first in the DOM so Tab order matches reading order (left →
 * right → primary last), not just the visual placement.
 */
export function DialogActions({
  primary,
  secondary,
  onClose,
  closeLabel,
}: {
  primary?: { label: string; onClick: () => void; disabled?: boolean; danger?: boolean };
  secondary?: { label: string; onClick: () => void; danger?: boolean };
  onClose?: () => void;
  closeLabel?: string;
}) {
  const t = useT();
  // Route the Cancel button through Dialog.tsx's own fading-close callback
  // (when nested inside a Dialog, which is the ~universal case) so a click
  // on Cancel fades the outer dialog out instead of triggering the parent's
  // raw `onClose` prop and skipping the exit animation the audit added.
  // Standalone use (outside any Dialog) falls back to `onClose` directly.
  const dialogClose = useDialogClose();
  return (
    <div className="dialog-actions">
      {onClose && (
        <button className="button" onClick={dialogClose ?? onClose}>
          {closeLabel ?? t("common.cancel")}
        </button>
      )}
      {secondary && (
        <button
          className={secondary.danger ? "button button-danger" : "button"}
          onClick={secondary.onClick}
        >
          {secondary.label}
        </button>
      )}
      {primary && (
        <button
          className={primary.danger ? "button button-danger" : "button button-primary"}
          onClick={primary.onClick}
          disabled={primary.disabled}
        >
          {primary.label}
        </button>
      )}
    </div>
  );
}
